import React, { useState } from 'react'
import moment from 'moment/moment'

import styled from 'styled-components'
import { Text } from 'components/v2/Text'
import { Row, Column } from 'components/v2/Grid'
import { Star } from 'phosphor-react'

import { arrayGroup } from 'utils/helpers'

const MAX_PAGES = 4
const SHOW_PER_PAGE = 4

const EvaluationRatingTotal = ({ evaluation }) => {
  return (
    <Circle>
      {String(evaluation?.rating?.total?.toFixed(1))
        .replace('.', ',')
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}
    </Circle>
  )
}

const EvaluationRating = ({ label, rating }) => {
  return (
    <Row alignItems='flex-start' justifyContent='space-between' width='100%'>
      <Text color='#FF7708' fontSize={14} fontWeight={600}>
        {label}:
      </Text>
      <Row alignItems='center' justifyContent='flex-start' gap='4px'>
        {[...Array(rating)].map((_, index) => (
          <Star
            key={index}
            size={16}
            color='#FF7708'
            weight='fill'
            style={{ marginBottom: '12px' }}
          />
        ))}
      </Row>
    </Row>
  )
}

const EvaluationComment = ({ comment }) => {
  const [showMore, setShowMore] = useState(false)

  const handleShowMore = () => {
    setShowMore(!showMore)
  }

  return (
    <Column alignItems='flex-start' justifyContent='flex-start' gap='8px' width='100%'>
      <Text color='black' fontSize={13} fontWeight={600} textAlign='left' lineHeight='1.4'>
        {comment.length > 150 && !showMore ? (
          `${comment.substring(0, 150)}...`
        ) : (
          <Text color='black' fontSize={13} fontWeight={600} textAlign='left' lineHeight='1.4'>
            {comment}
          </Text>
        )}
      </Text>
      {comment.length > 150 && (
        <Text
          color='#FF7708'
          fontSize={12}
          fontWeight={600}
          onClick={handleShowMore}
          cursor='pointer'
          textAlign='left'
          height='75px'
          style={{ cursor: 'pointer' }}
        >
          {showMore ? 'Ver menos' : 'Ver mais'}
        </Text>
      )}
    </Column>
  )
}
const EvaluationCard = ({ evaluation }) => {
  return (
    <EvaluationCardContainer>
      <Row height='100%' width='100%'>
        <Column mr='16px'>
          <EvaluationRatingTotal evaluation={evaluation} />
        </Column>
        <EvaluationComment comment={evaluation.comment} />
      </Row>
      <Column alignItems='flex-start' width='100%'>
        <EvaluationRating label='Atenção' rating={evaluation.rating.attention} />
        <EvaluationRating label='Ambiente' rating={evaluation.rating.knowledge} />
        <EvaluationRating label='Pontualidade' rating={evaluation.rating.punctuality} />
      </Column>
      <Column alignItems='flex-start'>
        <Text color='black' fontSize={14} fontWeight={600}>
          {evaluation.patient}
        </Text>
        <Text color='gray.n400' fontSize={14} fontWeight={600}>
          {moment.unix(evaluation.date).format('DD/MM/YYYY - HH:mm')}
        </Text>
      </Column>
    </EvaluationCardContainer>
  )
}

export const EvaluationsCarousel = ({ psychoEvaluations }) => {
  const [page, setPage] = useState(1)

  const evaluations = arrayGroup(psychoEvaluations, SHOW_PER_PAGE)
  const totalPages =
    MAX_PAGES > Math.ceil(psychoEvaluations?.length / SHOW_PER_PAGE)
      ? Math.ceil(psychoEvaluations?.length / SHOW_PER_PAGE)
      : MAX_PAGES

  const handlePageChange = page => {
    setPage(page)
  }

  return (
    <Container>
      <Content>
        <Column alignItems='flex-start' justifyContent='flex-start' gap='16px'>
          <Text color='primary' fontSize={24} fontWeight={600} textAlign='left'>
            {psychoEvaluations?.length > 0 ? 'Avaliações mais recentes' : 'Nenhuma avaliação'}
          </Text>
          <Text color='gray.n700' fontSize={16} fontWeight={600} textAlign='left'>
            {psychoEvaluations?.length > 0
              ? 'Confira aqui os feedbacks dos pacientes que já passaram com esse psicólogo'
              : ''}
          </Text>
        </Column>
        <Carousel>
          {evaluations[page - 1]?.map((evaluation, index) => (
            <EvaluationCard key={index} evaluation={evaluation} />
          ))}
        </Carousel>
        <DotsContainer>
          {[...Array(totalPages)].map((_, index) => (
            <Dot
              key={index}
              active={index + 1 === page}
              onClick={() => handlePageChange(index + 1)}
            />
          ))}
        </DotsContainer>
      </Content>
    </Container>
  )
}
const Container = styled.div`
  width: 100%;
  background-color: #fafafa;
  padding: 50px 0;
`

const Content = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 40px;
  }
`

const Carousel = styled.div`
  display: flex;
  justify-content: center;
  overflow-x: auto;
  scroll-behavior: smooth;
  margin-top: 20px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  ::-webkit-scrollbar,
  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #ff7708;
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`

const EvaluationCardContainer = styled.div`
  background: #fff;
  margin: 10px;
  padding: 24px;
  width: 33%;
  max-width: 300px;
  min-width: 300px;

  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  gap: 1rem;

  border-radius: 4px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
`

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => (props.active ? '#FF7708' : '#e0e0e0')};
`

const Circle = styled.div`
  width: 45px;
  height: 45px;

  border-radius: 50%;

  background-color: #ff7708;
  color: #fff;

  display: flex;
  justify-content: center;
  align-items: center;

  font-size: 16px;
  font-weight: 600;
`
